import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { Users, BookOpen, MapPin, Activity, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function Dashboard() {
  const api = useApi();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/stats')
      .then(d => {
        if (d && !d.error) setStats(d);
        else if (d?.error) setError(d.error);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const cityData = stats?.top_cities?.map(c => ({
    name: c.city || 'Unknown',
    users: parseInt(c.cnt)
  })) || [];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="section-title">Dashboard</div>
          <div className="section-sub">Khush Aamdeed, {user?.username}! 🌹 Bot ka overview dekhein.</div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <span className="spinner" style={{ width: 32, height: 32 }} />
        </div>
      ) : error ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--danger)' }}>
          ⚠️ Stats load nahi hui: {error}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
            <StatCard icon={<Users size={20} />}     color="var(--emerald-800)" border="var(--emerald-600)" value={stats?.total_users ?? 0}  label="Total Users" />
            <StatCard icon={<TrendingUp size={20} />} color="var(--gold-900)"    border="var(--gold-700)"    value={stats?.today_users ?? 0}  label="Aaj Ke Users" iconColor="var(--gold-300)" />
            <StatCard icon={<BookOpen size={20} />}   color="var(--emerald-900)" border="var(--emerald-700)" value={stats?.total_books ?? 0}  label="Kitabein" />
            <StatCard icon={<Activity size={20} />}   color="#1a1040"            border="#3d2080"            value="Active" label="Bot Status" iconColor="#a080ff" />
          </div>

          {cityData.length > 0 && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapPin size={14} style={{ color: 'var(--emerald-400)' }} /> Top Cities
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={cityData} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: 'var(--text-primary)' }}
                  />
                  <Bar dataKey="users" fill="var(--emerald-600)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="card">
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 14 }}>Quick Access</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { label: '⚡ Menu Control', to: '/menu-control' },
                { label: '📚 Books',        to: '/books'        },
                { label: '🎥 Videos',       to: '/videos'       },
                { label: '🕌 Cities',       to: '/cities'       },
                { label: '👥 Users',        to: '/users'        },
                { label: '📢 Broadcast',    to: '/broadcast'    },
                { label: '💰 Donate',       to: '/donate'       },
                { label: '📖 Masail',       to: '/masail'       },
                { label: '📱 Social',       to: '/social'       },
                { label: '⚙️ Settings',    to: '/settings'     },
              ].map(({ label, to }) => (
                <a key={to} href={to} className="btn btn-ghost btn-sm">{label}</a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon, color, border, value, label, iconColor = 'var(--emerald-300)' }) {
  return (
    <div className="stat-card">
      <div className="stat-icon" style={{ background: color, border: `1px solid ${border}`, color: iconColor }}>
        {icon}
      </div>
      <div>
        <div className="stat-value">{value}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}
